import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	LoButton,
	LoCard,
	LoIcon,
	LoSkeleton,
	LoStatus,
	LoToastService
} from 'lo-design-system';
import {
	UserBrandResponse,
	UserBrandService,
	UserBrandSource
} from '@brand-lolly/app/social-media-influencer/modules/chrome-extension/services/user-brand.service';
import {
	BrandProfileFormComponent,
	EditableBrand
} from '@brand-lolly/components/brand-profile-form/brand-profile-form.component';
import { AnaSettingAnchorDirective } from '@brand-lolly/components/ana-setting-anchor/ana-setting-anchor.directive';

const SOURCE_LABELS: Record<UserBrandSource, string> = {
	registration: 'Account brand',
	settings: 'Added by you',
	assistant: 'Saved by Ana'
};

const NEW_BRAND = 'new';
/** The row id of an account brand that exists only on the account (`users.company`), with no `user_brands` row yet. */
const ACCOUNT_ONLY_BRAND = 'account';

/** The account's own identity as My Profile reads it — `users.company`, `users.website`, `users.companylogo`. */
export interface AccountProfile {
	company: string | null;
	website: string | null;
	logo: string | null;
}

interface BrandRow {
	brand: UserBrandResponse;
	/** The brand's own logo; the account brand falls back to the account logo. */
	logo: string | null;
	/** The account brand — the one the account registered with; listed first, never removed. */
	isAccount: boolean;
	sourceLabel: string;
	website: string;
	socials: string;
	removable: boolean;
	editable: boolean;
	/** False for an account brand with no row yet — there is nothing to mark default. */
	defaultable: boolean;
}

/**
 * Settings, My Profile: every brand on the account — the one it registered with
 * (first), the ones added here or from the Chrome extension, and the ones Ana
 * saved from a conversation (all are `user_brands` rows), each with its own
 * logo. Adds, edits, removes and marks the default.
 */
@Component({
	standalone: true,
	selector: 'app-account-brands',
	templateUrl: './account-brands.component.html',
	styleUrls: ['./account-brands.component.scss'],
	imports: [
		CommonModule,
		LoButton,
		LoCard,
		LoIcon,
		LoSkeleton,
		LoStatus,
		BrandProfileFormComponent,
		AnaSettingAnchorDirective
	]
})
export class AccountBrandsComponent implements OnInit, OnChanges, OnDestroy {
	/** Whether this person may change the account brand — its name, website and logo (the owner only). */
	@Input() canEditAccountBrand = true;
	/** The account's own name, site and logo; null until My Profile has read them. */
	@Input() accountProfile: AccountProfile | null = null;
	/** The account brand was saved here — My Profile re-reads the account's name, site and logo. */
	@Output() accountBrandSaved = new EventEmitter<void>();
	/** Any other brand was saved, made default or removed — the header's default brand may have changed. */
	@Output() brandsChanged = new EventEmitter<void>();

	readonly newBrand = NEW_BRAND;
	rows: BrandRow[] = [];
	loading = true;
	editingId: string | null = null;
	editingBrand: EditableBrand | null = null;
	confirmRemoveId: string | null = null;
	busyId: string | null = null;

	private brands: UserBrandResponse[] = [];
	private unsubscriber = new Subject<void>();

	constructor(
		private userBrands: UserBrandService,
		private toasts: LoToastService
	) {}

	ngOnInit(): void {
		this.reload();
	}

	ngOnChanges(): void {
		if (!this.loading) {
			this.rows = this.toRows(this.brands);
		}
	}

	ngOnDestroy(): void {
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	reload(): void {
		this.userBrands
			.getAllBrands()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: brands => {
					this.brands = brands;
					this.rows = this.toRows(brands);
					this.loading = false;
				},
				error: () => {
					this.loading = false;
					this.toasts.error('Please try again.', {
						heading: 'Could not load your brands'
					});
				}
			});
	}

	trackById(_: number, row: BrandRow): string {
		return row.brand.id;
	}

	startAdd(): void {
		this.editingId = NEW_BRAND;
		this.editingBrand = null;
		this.confirmRemoveId = null;
	}

	startEdit(row: BrandRow): void {
		this.editingId = row.brand.id;
		// An account brand with no row yet is CREATED from the account's values: the form creates when the id is empty.
		this.editingBrand =
			row.brand.id === ACCOUNT_ONLY_BRAND
				? { ...row.brand, id: '' }
				: row.brand;
		this.confirmRemoveId = null;
	}

	cancelEdit(): void {
		this.editingId = null;
		this.editingBrand = null;
	}

	onSaved(brand: UserBrandResponse): void {
		this.editingId = null;
		this.editingBrand = null;
		this.toasts.success('Saved to your brand profile.');
		// CONTRACT: the brand PATCH/POST mirrors the account brand's name, site and logo onto the account itself.
		if (brand.source === 'registration') {
			this.accountBrandSaved.emit();
		} else {
			this.brandsChanged.emit();
		}
		this.reload();
	}

	makeDefault(row: BrandRow): void {
		this.busyId = row.brand.id;
		this.userBrands
			.updateBrand(row.brand.id, { is_default: true })
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: () => {
					this.busyId = null;
					this.brandsChanged.emit();
					this.reload();
				},
				error: () => {
					this.busyId = null;
					this.toasts.error('Please try again.', {
						heading: 'Could not change the default brand'
					});
				}
			});
	}

	askRemove(row: BrandRow): void {
		this.confirmRemoveId = row.brand.id;
	}

	cancelRemove(): void {
		this.confirmRemoveId = null;
	}

	remove(row: BrandRow): void {
		this.busyId = row.brand.id;
		this.userBrands
			.deleteBrand(row.brand.id)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: () => {
					this.busyId = null;
					this.confirmRemoveId = null;
					this.brandsChanged.emit();
					this.reload();
				},
				error: () => {
					this.busyId = null;
					this.toasts.error('Please try again.', {
						heading: 'Could not remove the brand'
					});
				}
			});
	}

	/** The account brand first, then the rest in the server's order (default first). */
	private toRows(brands: UserBrandResponse[]): BrandRow[] {
		const account = brands.find(brand => brand.source === 'registration');
		const others = brands
			.filter(brand => brand !== account)
			.map(brand => this.toRow(brand));
		if (account) {
			return [this.toRow(account), ...others];
		}
		const company = (this.accountProfile?.company ?? '').trim();
		if (!company) {
			return others;
		}
		return [this.toRow(this.accountOnlyBrand(company)), ...others];
	}

	/** An account registered before brand rows existed: its brand is only `users.company`. */
	private accountOnlyBrand(company: string): UserBrandResponse {
		return {
			id: ACCOUNT_ONLY_BRAND,
			user_id: 0,
			brand_name: company,
			brand_website: this.accountProfile?.website || null,
			brand_details_summary: '',
			brand_goals: null,
			commission_ceiling: null,
			excluded_partners: null,
			prohibitions: null,
			source: 'registration',
			social_accounts: [],
			is_default: false,
			// CONTRACT: the brands API takes only an http(s) logo; an older account logo stays on the account.
			logo_url: /^https?:\/\//i.test(this.accountProfile?.logo ?? '')
				? this.accountProfile?.logo ?? null
				: null,
			created_at: '',
			updated_at: ''
		};
	}

	private toRow(brand: UserBrandResponse): BrandRow {
		const registration = brand.source === 'registration';
		return {
			brand,
			logo:
				brand.logo_url ||
				(registration ? this.accountProfile?.logo || null : null),
			isAccount: registration,
			sourceLabel: SOURCE_LABELS[brand.source] ?? SOURCE_LABELS.settings,
			website: (brand.brand_website ?? '')
				.trim()
				.replace(/^https?:\/\//i, '')
				.replace(/^www\./i, '')
				.replace(/\/+$/, ''),
			socials: (brand.social_accounts ?? [])
				.map(account => account.platform + ' @' + account.handle)
				.join(' · '),
			removable: !registration,
			editable: !registration || this.canEditAccountBrand,
			defaultable: !brand.is_default && brand.id !== ACCOUNT_ONLY_BRAND
		};
	}
}
